import { when } from 'mobx';

import type { Campaign, Segment, Segments } from '@feathr/blackbox';
import { EmailBaseCampaign } from '@feathr/blackbox';

/** Reads the store rather than fetching, so groups created inline in the editor are included. */
function getAudienceSegments(campaign: Campaign, Segments: Segments): Segment[] {
  if (!(campaign instanceof EmailBaseCampaign)) {
    return [];
  }
  const { excludedSegmentIds, includedSegmentIds } = campaign.audienceSegmentIds;
  const segmentIds = new Set([...includedSegmentIds, ...excludedSegmentIds]);
  return Segments.models.filter((segment) => segmentIds.has(segment.id));
}

/**
 * `Segments.get()` fabricates a placeholder for an id it has not loaded, and a group that is
 * loading or failed to load stays both ephemeral and a placeholder. Only a group built in the
 * editor — ephemeral but never a placeholder — is new; one whose create failed stays that way,
 * so it is retried on the next save.
 */
function isNewSegment(segment: Segment): boolean {
  return segment.isEphemeral && !segment.isPlaceholder;
}

/**
 * Shared preSave function for campaign editing.
 * Saves ephemeral segments to the API before the campaign save references their ids, and returns
 * the ones it created so a caller holding a cached list of groups can add them to it. `onCreated`
 * fires per group as its create succeeds — unlike the return value, it still reaches the caller
 * when another create in the batch fails, so no created group is orphaned from cached lists.
 */
export async function ensureEphemeralSegmentsAreSaved(
  newCampaign: Campaign,
  Segments: Segments,
  onCreated?: (segment: Segment) => void,
): Promise<Segment[]> {
  const created = await Promise.all(
    getAudienceSegments(newCampaign, Segments).map(async (segment) => {
      await when(() => !segment.isPending);
      if (!isNewSegment(segment)) {
        return undefined;
      }
      // Refreshing the api cache would refetch the editor's group list mid-save, overwriting
      // unsaved edits to the other groups and clearing the dirty flags `save()` relies on.
      await Segments.add(segment, { refreshApiCache: false });
      if (segment.isErrored) {
        throw segment.error;
      }
      onCreated?.(segment);
      return segment;
    }),
  );
  return created.filter((segment): segment is Segment => !!segment);
}

/** New groups still needing a name; `Segment` requires one, so saving would fail validation. */
export function getUnnamedNewSegments(campaign: Campaign, Segments: Segments): Segment[] {
  return getAudienceSegments(campaign, Segments).filter(
    (segment) => isNewSegment(segment) && !segment.get('name'),
  );
}
